import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, InputNumber, DatePicker, Button, Row, Col, Upload, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { UploadFile } from 'antd/es/upload/interface';
import type { InvoiceListItem } from '../../store/api/financeApi';
import { useUpdateInvoiceMutation } from '../../store/api/financeApi';

export interface EditClientInvoiceModalProps {
  open: boolean;
  invoice: InvoiceListItem | null;
  onCancel: () => void;
  onSuccess?: () => void;
}

const EditClientInvoiceModal: React.FC<EditClientInvoiceModalProps> = ({
  open,
  invoice,
  onCancel,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [updateInvoice, { isLoading }] = useUpdateInvoiceMutation();

  useEffect(() => {
    if (!open || !invoice) return;
    form.setFieldsValue({
      number: invoice.number,
      invoiceDate: invoice.invoiceDate ? dayjs(invoice.invoiceDate) : dayjs(),
      amountWithVAT: Number(invoice.amountWithVAT ?? 0)
    });
    setFileList([]);
  }, [open, invoice, form]);

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    onCancel();
  };

  const handleSubmit = async () => {
    if (!invoice) return;
    const values = await form.validateFields();
    const file = fileList[0]?.originFileObj as File | undefined;
    try {
      await updateInvoice({
        id: invoice.id,
        number: String(values.number).trim(),
        invoiceDate: dayjs(values.invoiceDate).toISOString(),
        amountWithVAT: Number(values.amountWithVAT),
        file: file ?? null
      }).unwrap();
      message.success('Счёт обновлён');
      handleCancel();
      onSuccess?.();
    } catch (e: any) {
      if (e?.data?.code === 'INVOICE_EDIT_FORBIDDEN') {
        message.error(e?.data?.error || 'Редактирование недоступно для этого счёта');
        return;
      }
      message.error(e?.data?.error || 'Не удалось сохранить изменения');
    }
  };

  return (
    <Modal title="Редактировать счёт" open={open} onCancel={handleCancel} footer={null} destroyOnClose width={520}>
      <Form form={form} layout="vertical">
        <Form.Item name="number" label="Номер счёта" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="invoiceDate" label="Дата счёта" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="amountWithVAT"
              label="Сумма с НДС"
              rules={[{ required: true }, { type: 'number', min: 0.01 }]}
            >
              <InputNumber min={0.01} step={0.01} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Новый файл (необязательно)">
          <Upload beforeUpload={() => false} fileList={fileList} maxCount={1} onChange={({ fileList: fl }) => setFileList(fl)}>
            <Button icon={<UploadOutlined />}>Выбрать файл</Button>
          </Upload>
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Button onClick={handleCancel} style={{ width: '100%' }}>
              Отмена
            </Button>
          </Col>
          <Col span={12}>
            <Button type="primary" loading={isLoading} onClick={() => void handleSubmit()} style={{ width: '100%' }}>
              Сохранить
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default EditClientInvoiceModal;
